"use client";

import Link from "next/link";
import { ArrowLeft } from "lucide-react";
import { useTranslations } from "next-intl";

export function NotFound() {
  const t = useTranslations("NotFound");

  return (
    <div className="flex flex-col items-center justify-center min-h-screen w-full text-center px-6 relative overflow-hidden bg-(--bg-primary)">
      {/* Absolute Background Layers - Technical Accents */}
      <div className="absolute inset-0 pointer-events-none">
        {/* Faint Grid */}
        <div
          className="absolute inset-0 opacity-[0.03]"
          style={{
            backgroundImage: `linear-gradient(var(--text-secondary) 1px, transparent 1px), linear-gradient(90deg, var(--text-secondary) 1px, transparent 1px)`,
            backgroundSize: "40px 40px",
          }}
        />

        {/* Floating Technical Markers */}
        <div className="absolute top-20 left-10 text-[10px] font-mono text-(--text-secondary) opacity-20 hidden md:block">
          STATUS: OFFLINE
          <br />
          ERR_Connection_Reset
        </div>

        <div className="absolute bottom-20 right-10 text-[10px] font-mono text-(--text-secondary) opacity-20 hidden md:block text-right">
          SYS_OVERLOAD
          <br />
          BUFFER_SIZE: 0
        </div>

        {/* Corner Brackets */}
        <div className="absolute top-10 left-6 w-8 h-8 border-t border-l border-(--text-secondary) opacity-20 rounded-tl-lg" />
        <div className="absolute top-10 right-6 w-8 h-8 border-t border-r border-(--text-secondary) opacity-20 rounded-tr-lg" />
        <div className="absolute bottom-10 left-6 w-8 h-8 border-b border-l border-(--text-secondary) opacity-20 rounded-bl-lg" />
        <div className="absolute bottom-10 right-6 w-8 h-8 border-b border-r border-(--text-secondary) opacity-20 rounded-br-lg" />
      </div>

      <div className="relative z-10 flex flex-col items-center max-w-2xl">
        {/* Glitch 404 Container - No Zero, Just Cat */}
        <div className="relative mb-12 group">
          {/* The Layout: 4 [Cat] 4 */}
          <div className="flex items-center select-none">
            <span className="text-[120px] md:text-[200px] font-black leading-none text-(--brand-primary) opacity-90">
              4
            </span>

            <div className="relative animate-bounce-slow z-20 mx-4">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                version="1.1"
                xmlnsXlink="http://www.w3.org/1999/xlink"
                width="512"
                height="512"
                x="0"
                y="0"
                viewBox="0 0 64 64"
                xmlSpace="preserve"
                className="w-32 h-32 md:w-48 md:h-48 text-(--text-primary)"
              >
                <g>
                  <path
                    d="M42.624 19.495c.126-1.047-.944-3.225.64-3.55a1.007 1.007 0 0 1 1.11.87c.15 1.24.25 2.43.3 3.5.842.326 3.168 1.278 3.98 1.6.05-3.21-.39-9.81-4.21-14.42a1.003 1.003 0 0 0-1.66.19l-5.06 10.13a2.928 2.928 0 0 1 1.31.25zM19.304 20.315c.06-1.19.16-2.36.3-3.5a1.015 1.015 0 0 1 1.12-.87c1.569.354.532 2.407.65 3.54l2.64-1.06a5.608 5.608 0 0 1 2.26-.61l-5.07-10.13a1.014 1.014 0 0 0-1.67-.19c-3.81 4.61-4.25 11.21-4.2 14.42zM20.944 33.595a2.904 2.904 0 0 0 4.17 1.78l.43-.23a8.013 8.013 0 0 0-4.91-2.67zM43.044 33.595l.31-1.12a7.983 7.983 0 0 0-4.91 2.67l.43.23a2.904 2.904 0 0 0 4.17-1.78zM28.984 45.185l3.03 2.96 2.97-3z"
                    fill="currentColor"
                    opacity="1"
                  ></path>
                  <path
                    d="M59.612 53.222c-3.447-3.072-16.484-4.172-19.848-4.137a1 1 0 0 1 .02-2 79.52 79.52 0 0 1 11.55 1.14c.878-.936 1.327-2.253 2.798-1.663 1.052.116 2.113.454 3.18.65a.985.985 0 0 0 .96-.72 1.003 1.003 0 0 0-.69-1.24c-4.81-1.458-11.406-1.196-15.8-.082a.992.992 0 0 1-.318-1.945 34.658 34.658 0 0 1 11.65-.85c.71.08 1.42.18 2.15.31a44.628 44.628 0 0 0 4.56-11.76 2.968 2.968 0 0 0-2.4-3.57c-4.519-.765-7.488-1.662-9.1-3.3-.163-.296-1.392-.631-1.67-.79-2.683-1.007-6.106-2.572-8.82-3.45h-11.69a6.13 6.13 0 0 0-1.38.47c-2.654 1.093-6.078 2.427-8.72 3.5-2.03 1.834-4.6 2.78-9.47 3.57a2.943 2.943 0 0 0-2.4 3.57 44.625 44.625 0 0 0 4.56 11.76c.73-.13 1.44-.24 2.15-.31a34.62 34.62 0 0 1 11.64.85.992.992 0 0 1-.322 1.945 34.791 34.791 0 0 0-11.421-.755 26.625 26.625 0 0 0-4.378.837 1.003 1.003 0 0 0-.69 1.24.984.984 0 0 0 .96.72c1.076-.196 2.14-.534 3.19-.65 1.489-.594 1.908.73 2.801 1.663a79.437 79.437 0 0 1 11.52-1.14 1 1 0 0 1 .04 2c-3.367-.035-16.392 1.055-19.85 4.137a1 1 0 0 0 1.24 1.57c2.076-1.686 7.13-2.684 10.751-3.08a24.612 24.612 0 0 0 14.629 5.152v-7c-.141.037-3.218-3.15-3.4-3.31a1.985 1.985 0 0 1 1.4-3.37h6a1.985 1.985 0 0 1 1.4 3.37c-.185.177-3.246 3.332-3.4 3.31v7a24.647 24.647 0 0 0 14.638-5.153c3.617.398 8.658 1.394 10.74 3.08a1 1 0 0 0 1.24-1.57zM27.604 36.305l-1.54.83a4.923 4.923 0 0 1-7.05-3.01l-.64-2.33a1 1 0 0 1 .73-1.24c.19-.04 4.56-.99 8.8 4.24a1.014 1.014 0 0 1-.3 1.51zm8.78 0a1.014 1.014 0 0 1-.3-1.51c4.24-5.23 8.61-4.28 8.8-4.24a1 1 0 0 1 .73 1.24l-.64 2.33a4.923 4.923 0 0 1-7.05 3.01z"
                    fill="currentColor"
                    opacity="1"
                  ></path>
                </g>
              </svg>
            </div>

            <span className="text-[120px] md:text-[200px] font-black leading-none text-(--brand-primary) opacity-90">
              4
            </span>
          </div>
        </div>

        <h2 className="text-3xl md:text-5xl font-bold mb-6 tracking-tight font-outfit">
          {t("headline")}
        </h2>

        <p className="text-lg md:text-xl text-(--text-secondary) mb-12 max-w-xl leading-relaxed text-balance">
          {t("description")}
        </p>

        <Link
          href="/"
          className="group relative inline-flex items-center gap-3 px-8 py-4 rounded-full bg-(--text-primary) text-(--bg-primary) font-bold uppercase tracking-widest text-sm hover:scale-105 transition-transform duration-300 shadow-xl hover:shadow-2xl"
        >
          <ArrowLeft className="w-5 h-5 group-hover:-translate-x-1 transition-transform" />
          <span>{t("back_button")}</span>
        </Link>

        {/* Tech Decor under button */}
        <div className="mt-16 relative">
          <div className="absolute left-1/2 -translate-x-1/2 top-0 w-32 h-px bg-linear-to-r from-transparent via-(--text-secondary)/20 to-transparent" />
          <div className="mt-4 text-xs font-mono text-(--text-tertiary) uppercase tracking-widest opacity-60">
            {t("error_label")}: 404_PAGE_NOT_FOUND // SYSTEM_CAT_FAILURE
          </div>
        </div>
      </div>
    </div>
  );
}
